import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  FunctionComponent,
} from 'react'
import { LockStatus, MITee, MIHole } from '@app/evaluation-core/types'
import {
  CopyTeeAllHolesEvaluationProgress,
  CopyTeeAllHolesTaskResult,
  CopyTeeHoleState,
  copyTee,
  copyTeeAllHoles,
  subscribeCopyTeeAllHolesProgress,
} from '@app/evaluation-core'
import { useCopiedTee } from './CopiedTeeContext'
import { useSnackbar } from '@app/snackbar'
import { CopyTeeConfirmationModal } from './CopyTeeConfirmationModal'
import useEvaluation from '@common/hooks/useEvaluation'
import { EvalStatus } from '@app/courses/domain/ratingStatus'
import CopyTeeProgressModal from './CopyTeeProgressModal'

type OwnProps = {
  evaluationId: string
  currentHoleIndex: number
  currentTeeIndex: number
  tees: MITee[]
  // The target tee chosen from the (now-closed) menu. Null means no flow active.
  selectedTee: MITee | null
  onClearSelectedTee: () => void
}

type Props = OwnProps

const getInitialHoleStatuses = (
  holes: MIHole[]
): Record<string, CopyTeeHoleState> => {
  return holes.reduce((acc, hole, index) => {
    acc[String(index)] =
      hole?.locked === LockStatus.LOCKED ? 'SKIPPED_LOCKED' : 'NOT_STARTED'
    return acc
  }, {} as Record<string, CopyTeeHoleState>)
}

// Owns the copy-tee confirmation/progress modals and the copy logic. Rendered as
// a sibling of the tee-options DropDownMenu (not inside it) so the menu can close
// the moment a target is picked while these modals stay mounted.
export const CopyTeeFlow: FunctionComponent<Props> = ({
  evaluationId,
  currentHoleIndex,
  currentTeeIndex,
  tees,
  selectedTee,
  onClearSelectedTee,
}) => {
  const [_, __, ___, ____, _____, ______, setTeeLoading, clearTeeLoading] =
    useCopiedTee()
  const [openSnackBar] = useSnackbar()
  const { evaluation } = useEvaluation()

  const [isProgressModalOpen, setIsProgressModalOpen] = useState(false)
  const [isCopyAllHolesRunning, setIsCopyAllHolesRunning] = useState(false)
  const [progressTargetTee, setProgressTargetTee] = useState<MITee | null>(null)
  const [copyProgress, setCopyProgress] =
    useState<CopyTeeAllHolesEvaluationProgress | null>(null)
  const [copyResult, setCopyResult] =
    useState<CopyTeeAllHolesTaskResult | null>(null)
  const [copyProgressError, setCopyProgressError] = useState<string | null>(
    null
  )
  const [holeStatuses, setHoleStatuses] = useState<
    Record<string, CopyTeeHoleState>
  >({})

  const sourceTee = useMemo(() => {
    return tees.find((tee) => tee.teeIndex === currentTeeIndex) || null
  }, [tees, currentTeeIndex])

  const closeProgressModal = useCallback(() => {
    if (isCopyAllHolesRunning) {
      return
    }

    setIsProgressModalOpen(false)
    setProgressTargetTee(null)
    setCopyProgress(null)
    setCopyResult(null)
    setCopyProgressError(null)
    setHoleStatuses({})
  }, [isCopyAllHolesRunning])

  useEffect(() => {
    if (!isProgressModalOpen) {
      return
    }

    return subscribeCopyTeeAllHolesProgress(evaluationId, (nextProgress) => {
      if (!nextProgress) {
        return
      }

      setCopyProgress((previousProgress) => {
        const previousHoleStatuses =
          previousProgress?.progress?.holeStatuses || {}
        const nextHoleStatuses = nextProgress.progress?.holeStatuses || {}
        const mergedHoleStatuses = {
          ...previousHoleStatuses,
          ...nextHoleStatuses,
        }

        const mergedProgress = nextProgress.progress
          ? {
              ...nextProgress.progress,
              holeStatuses: mergedHoleStatuses,
            }
          : previousProgress?.progress
          ? {
              ...previousProgress.progress,
              holeStatuses: mergedHoleStatuses,
            }
          : null

        return {
          ...nextProgress,
          progress: mergedProgress,
        }
      })

      if (nextProgress.progress?.holeStatuses) {
        const nextHoleStatuses = nextProgress.progress.holeStatuses
        setHoleStatuses((previousStatuses) => {
          return {
            ...previousStatuses,
            ...nextHoleStatuses,
          }
        })
      }

      if (nextProgress.state === 'ERROR') {
        setCopyProgressError(
          nextProgress.error?.message ||
            'Copy tee across all unlocked holes failed. Please try again.'
        )
      }
    })
  }, [evaluationId, isProgressModalOpen])

  const handleCopyTee = async (
    tee: MITee,
    allUnlockedHoles: boolean,
    resetSource = false
  ) => {
    if (evaluation?.status === EvalStatus.FINALIZED) {
      openSnackBar('Cannot copy tee adjustments on a finalized evaluation.')
      onClearSelectedTee()
      return
    }

    setTeeLoading(tee.teeIndex)
    onClearSelectedTee()

    if (allUnlockedHoles) {
      setProgressTargetTee(tee)
      setIsProgressModalOpen(true)
      setIsCopyAllHolesRunning(true)

      setCopyProgress(null)
      setCopyResult(null)
      setCopyProgressError(null)
      setHoleStatuses(getInitialHoleStatuses(evaluation?.holes || []))
    }

    try {
      if (allUnlockedHoles) {
        const result = await copyTeeAllHoles(
          evaluationId,
          currentTeeIndex,
          tee.teeIndex,
          resetSource
        )

        setCopyResult(result)

        if (result.state === 'failed') {
          const errorMessage =
            result.errorMessage ||
            'Copy tee across all unlocked holes failed. Please try again.'

          setCopyProgressError(errorMessage)
          openSnackBar(errorMessage)
          return
        }

        if (result.state === 'completed_with_errors') {
          const failedHoles = result.failedHoles
            .map((holeIndex) => holeIndex + 1)
            .join(', ')

          openSnackBar(
            `Copied ${result.processed}/${
              result.totalUnlocked
            } unlocked holes. Failed holes: ${failedHoles || 'none'}.`
          )
          return
        }

        openSnackBar(
          `Copied tee adjustments on all unlocked holes (${result.processed}/${result.totalUnlocked}).`
        )
        return
      }

      await copyTee(
        evaluationId,
        currentHoleIndex,
        currentTeeIndex,
        tee.teeIndex,
        undefined,
        resetSource
      )
      openSnackBar(
        `Tee adjustments have been copied. Please check the target tee so everything looks as expected.`
      )
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Could not copy tee adjustments. Please try again.'

      if (allUnlockedHoles) {
        setCopyProgressError(errorMessage)
      }

      openSnackBar(errorMessage)
    } finally {
      if (allUnlockedHoles) {
        setIsCopyAllHolesRunning(false)
      }

      clearTeeLoading(tee.teeIndex)
    }
  }

  return (
    <>
      {selectedTee && (
        <CopyTeeConfirmationModal
          showModal={!!selectedTee}
          closeModal={onClearSelectedTee}
          acceptCurrentHoleFunction={(resetSource) =>
            handleCopyTee(selectedTee, false, resetSource)
          }
          acceptAllUnlockedHolesFunction={(resetSource) =>
            handleCopyTee(selectedTee, true, resetSource)
          }
          targetTeeNumber={selectedTee.teeNumber}
          targetTeeName={selectedTee.name}
        />
      )}
      <CopyTeeProgressModal
        isOpen={isProgressModalOpen}
        isRunning={isCopyAllHolesRunning}
        onClose={closeProgressModal}
        sourceTee={sourceTee}
        targetTee={progressTargetTee}
        holes={evaluation?.holes || []}
        holeStatuses={holeStatuses}
        progress={copyProgress}
        result={copyResult}
        errorMessage={copyProgressError}
      />
    </>
  )
}

export default CopyTeeFlow
